import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { flattenLeafCategories } from '@/temu-platform/utils/temuCategoryTree';
import { pickField } from '@/temu-platform/utils/temuResponse';

/** 与后端 SKC_META_MAX 对齐 */
const LOCAL_META_BATCH = 500;

export function applyProductMetaRow(map, skc, meta = {}) {
  const key = String(skc || '').trim();
  if (!key) return;
  const next = {
    title: meta.title || '',
    imageUrl: meta.imageUrl || '',
    spuName: meta.spuName ? String(meta.spuName) : '',
    productTypeId: meta.productTypeId ? String(meta.productTypeId) : '',
    categoryId: meta.categoryId ? String(meta.categoryId) : '',
    categoryPathLabel: meta.categoryPathLabel || '',
    skcShelfStatus: meta.skcShelfStatus !== '' && meta.skcShelfStatus != null ? meta.skcShelfStatus : '',
    supplierCode: meta.supplierCode ? String(meta.supplierCode) : '',
  };
  if (!map[key]) {
    map[key] = next;
    return;
  }
  const prev = map[key];
  map[key] = {
    title: prev.title || next.title,
    imageUrl: prev.imageUrl || next.imageUrl,
    spuName: prev.spuName || next.spuName,
    productTypeId: prev.productTypeId || next.productTypeId,
    categoryId: prev.categoryId || next.categoryId,
    categoryPathLabel: prev.categoryPathLabel || next.categoryPathLabel,
    skcShelfStatus: prev.skcShelfStatus !== '' && prev.skcShelfStatus != null
      ? prev.skcShelfStatus
      : next.skcShelfStatus,
    supplierCode: prev.supplierCode || next.supplierCode,
  };
}

export async function enrichCategoryPathLabels(shopId, map, categories) {
  const entries = Object.values(map || {});
  const needs = entries.some((m) => !m?.categoryPathLabel && (m?.productTypeId || m?.categoryId));
  if (!shopId || !needs) return map;
  try {
    let tree = categories;
    if (!tree) {
      const res = await temuPlatformAPI.getShopCategories(shopId);
      tree = res?.categories ?? res?.items ?? res?.list ?? res ?? [];
    }
    const leaves = flattenLeafCategories(tree);
    Object.keys(map).forEach((skc) => {
      const meta = map[skc];
      if (!meta || meta.categoryPathLabel) return;
      const hit = leaves.find((leaf) => (
        (meta.categoryId && String(leaf.categoryId) === String(meta.categoryId))
        || (meta.productTypeId && String(leaf.productTypeId) === String(meta.productTypeId))
      ));
      if (hit?.label) meta.categoryPathLabel = hit.label;
    });
  } catch {
    /* 类目文案补全失败不影响主流程 */
  }
  return map;
}

/**
 * 产品信息：只读本地 platform_product。列表页禁止回源 searchProduct（每批 10 条且会拉库存）。
 */
export async function loadProductMetaBySkc(shopId, skcList, options = {}) {
  if (!shopId || !skcList?.length) return {};
  const wanted = [...new Set(skcList.map((s) => String(s || '').trim()).filter(Boolean))];
  if (!wanted.length) return {};
  const map = {};
  for (let i = 0; i < wanted.length; i += LOCAL_META_BATCH) {
    const batch = wanted.slice(i, i + LOCAL_META_BATCH);
    try {
      const res = await temuPlatformAPI.listProductMetaBySkc(shopId, batch);
      const items = Array.isArray(res?.items) ? res.items : [];
      items.forEach((p) => {
        applyProductMetaRow(map, pickField(p, 'skcName', 'platformSkcId', 'skc'), {
          title: pickField(p, 'title', 'productName', 'productTitle') || '',
          imageUrl: pickField(p, 'imageUrl', 'primaryImageUrl', 'mainImageUrl') || '',
          spuName: pickField(p, 'spuName', 'platformSpuId', 'platformSpuName', 'spu') || '',
          productTypeId: pickField(p, 'productTypeId', 'product_type_id') || '',
          categoryId: pickField(p, 'categoryId', 'category_id') || '',
          categoryPathLabel: pickField(p, 'categoryPathLabel', 'category_path_label', 'categoryPath') || '',
          skcShelfStatus: pickField(p, 'skcShelfStatus', 'auditStatus', 'shelfStatus'),
          supplierCode: pickField(p, 'supplierCode', 'supplier_code', 'supplierId') || '',
        });
      });
    } catch {
      /* 单批本地查询失败跳过，列表仍可展示 SKC */
    }
  }
  if (options.enrichCategoryPath !== false) {
    await enrichCategoryPathLabels(shopId, map, options.categories);
  }
  return map;
}
